import type { PlatformID } from '@common/clients/api';
import { type ContextData } from '@common/defaults/ContextData';
import { hostHostIDMap } from '@common/defaults/getTheme';
import { type HostID } from '@common/types/HostID';
import type { ThemeID } from '@common/types/ThemeID';
import { useContextData } from '@common/useContextData';

/**
 * This interface can be used in combination with the `useThemeConfig` hook to
 * define configuration which can be overridden based on the current platform
 * or the current theme.
 */
export interface ThemeConfig<T> {
    /**
     * Configuration defaults.
     */
    defaults: T;
    /**
     * Configuration overrides for specific platforms.
     */
    byPlatform?: Partial<Record<PlatformID, Partial<T>>>;
    /**
     * Configuration overrides for specific themes.
     */
    byTheme?: Partial<Record<ThemeID, Partial<T>>>;
    /**
     * Configuration overrides for specific themes excluding the admin theme.
     */
    byHost?: Partial<Record<HostID, Partial<T>>>;
}

interface GetThemeConfigByContextParams<T> {
    config: ThemeConfig<T>;
    contextData: ContextData;
    overrides?: Partial<T> & Record<string, unknown>;
}

/**
 * Core function to resolve configuration based on platform and theme IDs
 * Can be used directly in server components.
 */
export const getThemeConfigByContext = <T>({
    config,
    contextData,
    overrides,
}: GetThemeConfigByContextParams<T>): T => {
    const platformConfig =
        contextData.platform.id && config.byPlatform ? config.byPlatform[contextData.platform.id] : undefined;
    const themeConfig =
        contextData.themeID && config.byTheme ? config.byTheme[contextData.themeID] : undefined;
    const hostConfig =
        contextData.host && config.byHost ? config.byHost[hostHostIDMap[contextData.host]] : undefined;
    return { ...config.defaults, ...platformConfig, ...themeConfig, ...hostConfig, ...overrides };
};

/**
 * Returns configuration for the current platform or theme.
 * Uses the getThemeConfigByContext function with the current context data.
 */
export const useThemeConfig = <T>(
    config: ThemeConfig<T>,
    overrides?: Partial<T> & Record<string, unknown>,
): T => {
    const contextData = useContextData();
    return getThemeConfigByContext({
        config,
        contextData,
        overrides,
    });
};
